import { useEffect, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { message } from 'antd'
import { useSocketStore } from '@stores/socketStore'

export const useWebSocket = () => {
  const { socket } = useSocketStore()
  const queryClient = useQueryClient()

  const handleDeviceOnline = useCallback((data: { device_id: string }) => {
    message.success(`设备 ${data.device_id} 已上线`)
    queryClient.invalidateQueries({ queryKey: ['devices'] })
    queryClient.invalidateQueries({ queryKey: ['statistics'] })
  }, [queryClient])

  const handleDeviceOffline = useCallback((data: { device_id: string; timestamp: string }) => {
    message.warning(`设备 ${data.device_id} 已离线`)
    queryClient.invalidateQueries({ queryKey: ['devices'] })
    queryClient.invalidateQueries({ queryKey: ['statistics'] })
  }, [queryClient])

  const handleOTAStatus = useCallback((data: {
    device_id: string
    status: string
    message: string
    data?: { from_version: string; to_version: string }
  }) => {
    if (data.status === 'success') {
      message.success(`设备 ${data.device_id} OTA升级成功`)
    } else if (data.status === 'failed') {
      message.error(`设备 ${data.device_id} OTA升级失败: ${data.message}`)
    } else {
      message.info(`设备 ${data.device_id} ${data.message}`)
    }
    queryClient.invalidateQueries({ queryKey: ['ota'] })
    queryClient.invalidateQueries({ queryKey: ['devices'] })
  }, [queryClient])

  const handleDeviceHeartbeat = useCallback((data: {
    device_id: string
    heartbeat: Record<string, any>
  }) => {
    // 更新特定设备的心跳数据
    queryClient.setQueryData(['device', data.device_id], (old: any) => {
      if (!old) return old
      return { ...old, heartbeat_data: data.heartbeat, is_online: true }
    })
  }, [queryClient])

  useEffect(() => {
    if (!socket) return

    socket.on('device_online', handleDeviceOnline)
    socket.on('device_offline', handleDeviceOffline)
    socket.on('ota_upgrade_status', handleOTAStatus)
    socket.on('device_heartbeat', handleDeviceHeartbeat)
    socket.on('device_status', (data) => {
      console.log('Device status update:', data)
      queryClient.invalidateQueries({ queryKey: ['device', data.device_id] })
    })

    return () => {
      socket.off('device_online', handleDeviceOnline)
      socket.off('device_offline', handleDeviceOffline)
      socket.off('ota_upgrade_status', handleOTAStatus)
      socket.off('device_heartbeat', handleDeviceHeartbeat)
      socket.off('device_status')
    }
  }, [socket, handleDeviceOnline, handleDeviceOffline, handleOTAStatus, handleDeviceHeartbeat, queryClient])
}
